import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Settings, User, ChevronDown, Moon, Sun, Menu } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useCompany } from '../../contexts/CompanyContext';
import CompanySwitcher from './CompanySwitcher';
import NotificationCenter from './NotificationCenter';

const Header = () => {
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { toggle: toggleSidebar } = useSidebar();
  const { theme, toggleTheme } = useTheme();
  const { effectiveAccountType } = useCompany();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  const handleSignOut = async () => {
    await signOut();

    // Redirect based on effective account type (derived from current company role)
    const redirectPaths = {
      superadmin: '/super-admin',
      corporate: '/corporate/login',
      contractor: '/contractor/login'
    };

    navigate(redirectPaths?.[effectiveAccountType] || '/');
  };

  // Get role display name based on effective account type
  const getRoleDisplay = () => {
    const roleNames = {
      superadmin: 'Super Admin',
      corporate: 'Corporate User',
      contractor: 'Contractor'
    };
    return roleNames?.[effectiveAccountType] || 'User';
  };

  // Get settings path based on effective account type
  const getSettingsPath = () => {
    const settingsPaths = {
      superadmin: '/super-admin/settings',
      corporate: '/corporate/settings',
      contractor: '/contractor/settings'
    };
    return settingsPaths?.[effectiveAccountType] || '/contractor/settings';
  };

  // Get dashboard path based on effective account type
  const getDashboardPath = () => {
    const dashboardPaths = {
      superadmin: '/super-admin/dashboard',
      corporate: '/corporate/dashboard',
      contractor: '/contractor/dashboard'
    };
    return dashboardPaths?.[effectiveAccountType] || '/contractor/dashboard';
  };


  return (
    <header className="fixed top-0 left-0 right-0 bg-background border-b border-border z-100">
      <div className="flex items-center justify-between px-4 lg:px-6 py-4">
        {/* Left Section - Hamburger + Logo */}
        <div className="flex items-center space-x-3">
          {/* Hamburger Menu - Mobile Only */}
          <button
            onClick={toggleSidebar}
            className="lg:hidden p-2 text-text-secondary hover:text-text-primary hover:bg-surface rounded-lg transition-colors"
            aria-label="Toggle sidebar menu"
          >
            <Menu size={24} />
          </button>

          {/* Logo (clickable) */}
          <button
            onClick={() => navigate(getDashboardPath())}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">M</span>
            </div>
            <div className="text-left hidden sm:block">
              <h1 className="text-lg font-bold text-text-primary">Mervo</h1>
              <p className="text-xs text-text-secondary">{getRoleDisplay()}</p>
            </div>
          </button>
        </div>

        {/* Right Section - User Actions */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Company Switcher */}
          <CompanySwitcher />

          {/* Notifications */}
          <NotificationCenter />

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface rounded-lg transition-colors"
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          {/* User Profile Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              className="flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-2 bg-surface hover:bg-border-light rounded-lg transition-colors touch-target-sm"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center overflow-hidden">
                {profile?.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={profile?.full_name || 'User'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={16} color="white" />
                )}
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-sm font-medium text-text-primary">{profile?.display_name || profile?.full_name || 'User'}</p>
                <p className="text-xs text-text-secondary">{profile?.email || ''}</p>
              </div>
              <ChevronDown size={16} className="text-text-secondary" />
            </button>

            {showProfileDropdown && (
              <div className="absolute top-full right-0 mt-2 w-56 sm:w-64 bg-surface border border-border rounded-lg shadow-xl z-50">
                <div className="p-4 border-b border-border">
                  <p className="text-text-primary font-medium">{profile?.full_name}</p>
                  <p className="text-sm text-text-secondary">{getRoleDisplay()}</p>
                </div>
                <div className="p-2">
                  <button
                    onClick={() => {
                      navigate(getSettingsPath());
                      setShowProfileDropdown(false);
                    }}
                    className="w-full flex items-center space-x-2 px-3 py-2 text-text-secondary hover:bg-surface-light rounded-lg transition-colors"
                  >
                    <Settings size={18} />
                    <span>Preferences</span>
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center space-x-2 px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <LogOut size={18} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;